/**
 * /settings/permissions/me — read-only "your effective permissions" page.
 *
 * Phase 5 UI checkpoint per AUTH-USER-PERMISSIONS-001-PHASE5-DECISIONS §8.
 * Self-service surface so a user can see what they're allowed to do
 * without hitting a 403 to find out.
 *
 * Renders the catalog grouped by category. Each row shows whether the
 * permission is granted, why (role default / explicit grant / explicit
 * revoke), and any threshold or scope filter attached to a grant.
 *
 * Out of scope (Phase 7 owns this):
 *   - granting / revoking — admin-only, separate page
 *   - resolving scope IDs to human names (right now we just show the
 *     ID list — Phase 7 adds an entity-name resolver)
 *   - threshold-currency formatting beyond USD
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import type { EffectivePermissionsResponse, EffectivePermission } from "@/lib/types";

export const Route = createFileRoute("/_auth/settings/permissions/me")({
  component: MyPermissionsPage,
});

const VIA_LABEL: Record<string, string> = {
  role_default: "Role default",
  explicit_grant: "Granted to you",
  explicit_revoke: "Revoked",
};

function MyPermissionsPage() {
  const { data, isLoading, error } = useQuery<EffectivePermissionsResponse>({
    queryKey: ["my-permissions"],
    queryFn: () => api("/api/users/me/permissions"),
    staleTime: 60_000,
  });

  const [filter, setFilter] = useState<"all" | "granted" | "denied">("all");

  // Group by category for rendering
  const grouped = useMemo(() => {
    if (!data) return [];
    const filtered = data.permissions.filter((p) => {
      if (filter === "granted") return p.granted;
      if (filter === "denied") return !p.granted;
      return true;
    });
    const map = new Map<string, EffectivePermission[]>();
    for (const p of filtered) {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data, filter]);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">Loading permissions…</div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-red-600">
        Failed to load permissions: {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;

  const grantedCount = data.permissions.filter((p) => p.granted).length;
  const totalCount = data.permissions.length;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Your permissions</h1>
        <p className="text-sm text-gray-600 mt-1">
          You have <span className="font-medium">{grantedCount}</span> of{" "}
          <span className="font-medium">{totalCount}</span> available
          permissions.
        </p>
        <div className="mt-2 text-sm text-gray-600 space-x-4">
          <span>
            Role:{" "}
            <span className="font-medium text-gray-900">{data.role}</span>
          </span>
          {data.legacy_role && (
            <span>
              Legacy role:{" "}
              <span className="font-medium text-gray-900">
                {data.legacy_role}
              </span>
            </span>
          )}
        </div>
      </div>

      <details className="mb-4 text-sm border rounded p-3 bg-gray-50">
        <summary className="cursor-pointer font-medium text-gray-700">
          How are these granted?
        </summary>
        <div className="mt-2 space-y-1 text-gray-700">
          <div>
            <strong>Role default</strong>: granted to you because of your role
            ({data.role}). Admins get every permission by default; viewers get
            read-only access; operators get a small set of safe defaults.
          </div>
          <div>
            <strong>Granted to you</strong>: an admin explicitly added this
            permission for you, sometimes with a spending limit (threshold) or
            scope (specific projects / entities).
          </div>
          <div>
            <strong>Revoked</strong>: an admin explicitly took this permission
            away — overrides the role default.
          </div>
        </div>
      </details>

      <div className="mb-4 flex gap-2 text-sm">
        {(["all", "granted", "denied"] as const).map((opt) => (
          <button
            key={opt}
            onClick={() => setFilter(opt)}
            className={
              "px-3 py-1 rounded border " +
              (filter === opt
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
            }
          >
            {opt === "all"
              ? `All (${totalCount})`
              : opt === "granted"
                ? `Granted (${grantedCount})`
                : `Denied (${totalCount - grantedCount})`}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {grouped.map(([category, perms]) => (
          <section
            key={category}
            className="border rounded-lg overflow-hidden"
          >
            <h2 className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {category}
            </h2>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">
                    Permission
                  </th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Source</th>
                  <th className="text-left px-4 py-2 font-medium">
                    Threshold / scope
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {perms.map((p) => (
                  <PermissionRow key={p.code} p={p} />
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}

function PermissionRow({ p }: { p: EffectivePermission }) {
  return (
    <tr>
      <td className="px-4 py-2 align-top">
        <div className="font-medium text-gray-900">{p.name}</div>
        <div className="text-xs text-gray-500 font-mono">{p.code}</div>
        <div className="text-xs text-gray-600 mt-1">{p.description}</div>
      </td>
      <td className="px-4 py-2 align-top">
        {p.granted ? (
          <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
            Granted
          </span>
        ) : (
          <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
            Denied
          </span>
        )}
      </td>
      <td className="px-4 py-2 align-top text-xs text-gray-600">
        {VIA_LABEL[p.via] ?? p.via}
      </td>
      <td className="px-4 py-2 align-top text-xs text-gray-600">
        <ThresholdAndScope p={p} />
      </td>
    </tr>
  );
}

function ThresholdAndScope({ p }: { p: EffectivePermission }) {
  const parts: string[] = [];
  if (p.threshold_value) {
    const unit = p.threshold_unit?.toUpperCase() ?? "USD";
    const fmt = formatThreshold(p.threshold_value, unit);
    parts.push(`Up to ${fmt}`);
  }
  if (p.scope_filter && Object.keys(p.scope_filter).length > 0) {
    const scope_descrs = Object.entries(p.scope_filter)
      .filter(([, ids]) => ids.length > 0)
      .map(([dim, ids]) => `${dimLabel(dim)} (${ids.length})`);
    if (scope_descrs.length > 0) {
      parts.push(`Scoped to ${scope_descrs.join(", ")}`);
    }
  }
  if (p.expires_at) {
    parts.push(`Expires ${new Date(p.expires_at).toLocaleDateString()}`);
  }
  return parts.length > 0 ? <span>{parts.join(" · ")}</span> : <span>—</span>;
}

function formatThreshold(value: string, unit: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value} ${unit}`;
  // Render USD with thousands separator, 0-decimal for whole numbers
  if (unit === "USD") {
    return `$${n.toLocaleString("en-US", {
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `${value} ${unit}`;
}

function dimLabel(dim: string): string {
  switch (dim) {
    case "entity_ids":
      return "entities";
    case "project_ids":
      return "projects";
    case "cost_code_ids":
      return "cost codes";
    case "vendor_ids":
      return "vendors";
    default:
      return dim;
  }
}
